import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { updateNotificationSettings, type NotificationSound } from '@auw/api-client';
import { useChatStore, useUserStore } from '@auw/stores';
import { Button, SelectDropdown, ToggleSwitch } from '../../common';
import { notificationKey, useNotificationSummary } from '../../notifications/useNotifications';
import { playNotificationSound } from '../../notifications/sounds';
import { SectionBlock, SectionHeader, SectionTitle, SettingRow, SettingInfo, SettingName, SettingDesc, Divider } from '../styles';

export function NotificationTab() {
  const { t } = useTranslation('common');
  const account = useUserStore(state => state.user?.userId ?? '');
  const query = useNotificationSummary();
  const client = useQueryClient();
  const [saving, setSaving] = useState(false);
  const settings = query.data?.settings ?? { sound_enabled: true, sound: 'chime' as const };
  async function save(patch: Parameters<typeof updateNotificationSettings>[0]) {
    setSaving(true);
    try {
      await updateNotificationSettings(patch);
      await client.invalidateQueries({ queryKey: notificationKey(account) });
    } catch {
      useChatStore.getState().addToast({ type: 'error', title: t('notifications.saveFailed') });
    } finally { setSaving(false); }
  }
  return <SectionBlock>
    <SectionHeader><SectionTitle>{t('notifications.title')}</SectionTitle></SectionHeader><Divider />
    <SettingRow>
      <SettingInfo><SettingName>{t('notifications.soundEnabled')}</SettingName>
        <SettingDesc>{t('notifications.soundDesc')}</SettingDesc></SettingInfo>
      <ToggleSwitch aria-label={t('notifications.soundEnabled')} checked={settings.sound_enabled}
        onChange={sound_enabled => void save({ sound_enabled })} disabled={saving || query.isPending || query.isError} />
    </SettingRow>
    <SettingRow>
      <SettingInfo><SettingName>{t('notifications.soundChoice')}</SettingName>
        <SettingDesc>{t('notifications.offlineSound')}</SettingDesc></SettingInfo>
      <div style={{ width: 200, maxWidth: '50%', flexShrink: 0 }}><SelectDropdown aria-label={t('notifications.soundChoice')} value={settings.sound}
        options={(['chime', 'bell', 'soft'] as const).map(value => ({ value, label: t('notifications.sounds.' + value) }))}
        disabled={saving || query.isPending || query.isError || !settings.sound_enabled}
        onChange={value => { playNotificationSound(value as NotificationSound); void save({ sound: value as NotificationSound }); }} /></div>
    </SettingRow>
    <Button variant="surface" size="sm" disabled={!settings.sound_enabled} onClick={() => playNotificationSound(settings.sound)}>{t('notifications.preview')}</Button>
    {query.isError && <SettingDesc role="alert">{t('notifications.loadFailed')}</SettingDesc>}
  </SectionBlock>;
}
